import React from 'react'
import { CustomerServiceTelephone } from '@mobi/component-library/Common/V2'
import { FeatureFlags } from '@mobi/settings'
import { ButtonBlock } from '@mobi/component-library/Common/Buttons'
import { Grid, GridRow, GridCell } from '@mobi/component-library/Common/Grid'
import { InputField } from '@mobi/component-library/Common/Input/InputField'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { appHistory } from '@core/Utils/routing/history'
import { MainContainer } from '@core/Components/Containers'
import { ErrorMessage } from '@core/Components/Messages'
import { NoticeBoxSingle } from '@core/Components/NoticeBox'
import { usePrevious, useFeature } from '@core/Utils/hooks'
import {
  routes,
  isValidBsb,
  formatBsb,
  isValidAccountName,
  isAccountNameIncludingSurname,
  isValidAccountNumber,
} from '@mobi/account/Areas/Withdraw/Utils'
import { Loading, Error } from './BankAccountComponents'
import { BankAccountContainerStyled, BankBranchStyled, BankNameStyled } from './BankAccount.styles'
import {
  isBankAccountRegistered,
  useBankDetailsQuery,
  postUpdateBankAccount,
  BankAccountResponse,
  useBankDetailsFromBsbQuery,
  isBankDetailsFromBsbFound,
} from './BankAccountApi'

type EntryFormBankDetails = Pick<BankAccountResponse, 'bsb' | 'accountName' | 'accountNumber'>
type ReadOnlyFormBankDetails = Pick<BankAccountResponse, 'bankName' | 'branchName'>

export const BankAccountEdit = (): JSX.Element | null => {
  const isTempDisableContactDetailsActive = useFeature(
    FeatureFlags.TEMP_DISABLE_EDIT_CONTACT_DETAILS.key
  )
  const [readOnlyForm, setReadOnlyForm] = React.useState<ReadOnlyFormBankDetails>({
    bankName: '',
    branchName: '',
  })

  const [entryForm, setEntryForm] = React.useState<EntryFormBankDetails>({
    bsb: '',
    accountName: '',
    accountNumber: '',
  })

  const [isShowErrorOnSave, setIsShowErrorOnSave] = React.useState<boolean>(false)
  const [errorBsb, setErrorBsb] = React.useState<string | boolean>(false)
  const [errorAccountName, setErrorAccountName] = React.useState<string | boolean>(false)
  const [errorAccountNumber, setErrorAccountNumber] = React.useState<string | boolean>(false)
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false)

  const {
    isLoading: bankDetailsIsLoading,
    isError: bankDetailsIsError,
    data: bankDetailsData,
  } = useBankDetailsQuery()

  const {
    data: bankDetailsFromBsbData,
    refetch: bankDetailsFromBsbRefetch,
    isFetched: bankDetailsFromBsbIsFetched,
  } = useBankDetailsFromBsbQuery(entryForm.bsb)

  const previousEntryFormBsb = usePrevious(entryForm.bsb)

  const isBsbRefetch =
    entryForm.bsb !== '' && previousEntryFormBsb !== entryForm.bsb && isValidBsb(entryForm.bsb)

  React.useEffect(() => {
    if (isBankAccountRegistered(bankDetailsData) && bankDetailsData !== undefined) {
      setReadOnlyForm({
        bankName: bankDetailsData.bankName,
        branchName: bankDetailsData.branchName,
      })

      setEntryForm({
        bsb: bankDetailsData.bsb,
        accountName: bankDetailsData.accountName,
        accountNumber: bankDetailsData.accountNumber,
      })
    }
  }, [bankDetailsData])

  React.useEffect(() => {
    if (!bankDetailsFromBsbIsFetched) {
      return
    }

    if (isBankDetailsFromBsbFound(bankDetailsFromBsbData)) {
      setReadOnlyForm({
        bankName: bankDetailsFromBsbData.bankName,
        branchName: bankDetailsFromBsbData.branchName,
      })
    } else {
      setReadOnlyForm({ bankName: '-', branchName: '-' })
      setErrorBsb('BSB not found')
    }
  }, [bankDetailsFromBsbData, bankDetailsFromBsbIsFetched])

  React.useEffect(() => {
    if (isBsbRefetch) {
      bankDetailsFromBsbRefetch()
    }
  }, [isBsbRefetch, bankDetailsFromBsbRefetch])

  const isSubmitDisabled =
    !!errorBsb ||
    !!errorAccountName ||
    !!errorAccountNumber ||
    !bankDetailsFromBsbIsFetched ||
    entryForm.bsb.trim().length === 0 ||
    entryForm.accountNumber.trim().length === 0 ||
    entryForm.accountName.trim().length === 0 ||
    isSubmitted

  const submitForm = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (isSubmitDisabled) {
      return
    }

    setIsSubmitted(true)

    postUpdateBankAccount({
      accountName: entryForm.accountName,
      accountNumber: entryForm.accountNumber,
      bsb: entryForm.bsb,
      bankName: readOnlyForm.bankName,
      branchName: readOnlyForm.branchName,
    })
      .then(response => {
        if (response.isSuccessful) {
          appHistory.push(routes.home)
        } else {
          setIsShowErrorOnSave(true)
          setIsSubmitted(false)
        }
      })
      .catch(() => {
        setIsShowErrorOnSave(true)
        setIsSubmitted(false)
      })
  }

  if (bankDetailsIsLoading || isSubmitted) {
    return <Loading />
  }

  if (bankDetailsIsError) {
    return <Error />
  }

  return (
    <MainContainer data-tid-bank-account-container=''>
      <PageHeader title='Edit Bank Account' />

      {isShowErrorOnSave && (
        <ErrorMessage>
          Unable to save bank details. Please try again later. <br />
          If the problem continues, please contact Customer Support on <CustomerServiceTelephone />
        </ErrorMessage>
      )}
      <BankAccountContainerStyled>
        <form
          action=''
          noValidate={true}
          data-tid-update-bank-account-form=''
          onSubmit={submitForm}
        >
          <BankNameStyled data-tid-bank-name=''>{readOnlyForm.bankName}</BankNameStyled>
          <BankBranchStyled data-tid-branch-name=''>{readOnlyForm.branchName}</BankBranchStyled>

          <InputField
            type='text'
            placeholder='BSB'
            value={entryForm.bsb}
            testId='bsb'
            name='BSB'
            required={true}
            maxLength={7}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              const inputValue = e.currentTarget.value
              const formattedBsb = formatBsb(inputValue)
              setEntryForm({ ...entryForm, bsb: formattedBsb })
              setErrorBsb(isValidBsb(inputValue) ? false : 'BSB format is 123-456')
            }}
            errorMessage={errorBsb}
            disabled={isTempDisableContactDetailsActive}
          />

          <InputField
            type='text'
            placeholder='Account Name'
            maxLength={40}
            value={entryForm.accountName}
            testId='account-name'
            name='Account Name'
            required={true}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              const value = e.currentTarget.value
              setEntryForm({ ...entryForm, accountName: value })
              setErrorAccountName(
                isValidAccountName(value) &&
                  isAccountNameIncludingSurname(value, bankDetailsData?.accountHolderSurname || '')
                  ? false
                  : 'Invalid Account Name'
              )
            }}
            errorMessage={errorAccountName}
            disabled={isTempDisableContactDetailsActive}
          />
          <InputField
            type='text'
            placeholder='Account Number'
            value={entryForm.accountNumber}
            testId='account-number'
            name='Account Number'
            required={true}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              const value = e.currentTarget.value
              setEntryForm({ ...entryForm, accountNumber: value })
              setErrorAccountNumber(isValidAccountNumber(value) ? false : 'Invalid Account Number')
            }}
            errorMessage={errorAccountNumber}
            disabled={isTempDisableContactDetailsActive}
          />

          {bankDetailsData?.isPendingWithdrawals && (
            <NoticeBoxSingle
              title='You currently have a pending withdrawal which will be directed to this bank account. If you change these details, all future withdrawals will be directed to your new bank account.'
              noticeBoxType='Warning'
              testId='bank-change-pending-withdrawal'
            />
          )}

          {isTempDisableContactDetailsActive ? (
            <NoticeBoxSingle
              title={
                <span>
                  Please contact our support team on <CustomerServiceTelephone /> to update your
                  bank account details.
                </span>
              }
              noticeBoxType='Info'
              hasBorder
            />
          ) : (
            <Grid margin='auto'>
              <GridRow>
                <GridCell padding='0.5rem' valign='middle'>
                  <ButtonBlock
                    type='reset'
                    onClick={() => appHistory.back()}
                    color='secondary'
                    testId='cancel-update-bank-btn'
                  >
                    Cancel
                  </ButtonBlock>
                </GridCell>
                <GridCell padding='0.5rem' valign='middle'>
                  <ButtonBlock
                    type='submit'
                    onClick={() => void 0}
                    color='primary'
                    testId='update-bank-btn'
                    disabled={isSubmitDisabled}
                  >
                    Save
                  </ButtonBlock>
                </GridCell>
              </GridRow>
            </Grid>
          )}
        </form>
      </BankAccountContainerStyled>
    </MainContainer>
  )
}
